import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";
import { profileApi } from "./api";
import { useMode } from "./mode";
import type { Me, Permission, Profile, User } from "./types";

const STORAGE_KEY = "uzp_profile";

function readProfileId(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Makes `id` the active profile the next time the dashboard shell mounts (and remembers it). */
export function rememberProfile(id: string) {
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // Storage unavailable (private window, blocked site data): the choice lasts for this tab.
  }
}

/** Cache keys of everything about one profile start with this, so profiles never share data. */
export const profileKey = (profileId: string) => ["profile", profileId] as const;

interface ProfileContextValue {
  user: User;
  profiles: Profile[];
  /** The active profile. */
  profile: Profile;
  switchProfile: (id: string) => void;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

/**
 * The active profile, chosen from `me.profiles` (which must not be empty). The choice is kept in
 * localStorage; an id that is no longer one of the user's profiles falls back to the first.
 */
export function ProfileProvider({ me, children }: { me: Me; children: ReactNode }) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState(readProfileId);
  const profile = me.profiles.find((p) => p.id === selected) ?? me.profiles[0]!;

  const switchProfile = useCallback(
    (id: string) => {
      if (id === profile.id) return;
      rememberProfile(id);
      setSelected(id);
      // Drop the previous profile's data; the next one loads under its own keys.
      queryClient.removeQueries({ queryKey: profileKey(profile.id) });
    },
    [profile.id, queryClient],
  );

  const value = useMemo(
    () => ({ user: me.user, profiles: me.profiles, profile, switchProfile }),
    [me.user, me.profiles, profile, switchProfile],
  );
  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

export function useProfile(): ProfileContextValue {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error("useProfile must be used inside <ProfileProvider>");
  return ctx;
}

/** Whether the user's role in the active profile allows `permission`. */
export function usePermission(permission: Permission): boolean {
  return useProfile().profile.permissions.includes(permission);
}

/**
 * The active profile's API client, the data mode, and its cache keys:
 * `key(...)` for test/live data (["profile", id, mode, ...]) and `teamKey(...)` for data that is
 * the same in both modes (["profile", id, ...]).
 */
export function useProfileApi() {
  const { profile } = useProfile();
  const { mode } = useMode();
  return useMemo(() => {
    const base = profileKey(profile.id);
    return {
      profileId: profile.id,
      mode,
      api: profileApi(profile.id),
      key: (...parts: unknown[]) => [...base, mode, ...parts],
      teamKey: (...parts: unknown[]) => [...base, ...parts],
    };
  }, [profile.id, mode]);
}
